import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { Loader2 } from "lucide-react";
import type { StockNewsItemData } from "@crypto-trading/common/browser";
import { AssetClassToggle } from "@/components/AssetClassToggle";
import { StockNewsTile } from "@/components/StockNewsTile";
import { StockNewsDetailDrawer } from "@/components/StockNewsDetailDrawer";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import {
  useInfiniteStockNews,
  useStockInstruments,
} from "@/hooks/useStockNews";
import { buildStockTradeTarget } from "@/stock/stock-trade";

const PAGE_SIZE = 8;

export const StockAlertsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [selectedItem, setSelectedItem] = useState<StockNewsItemData | null>(
    null
  );
  const [tradeOpen, setTradeOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [tradeTicker, setTradeTicker] = useState<string | null>(null);

  const { data: instruments = [] } = useStockInstruments();
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteStockNews(userId, PAGE_SIZE);
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const tradeTarget = useMemo(
    () =>
      tradeTicker ? buildStockTradeTarget(tradeTicker, instruments) : null,
    [tradeTicker, instruments]
  );

  const openTrade = useCallback(
    (ticker: string) => {
      const target = buildStockTradeTarget(ticker, instruments);
      setTradeTicker(ticker);
      setTradeSymbol(target.symbol);
      setSelectedItem(null);
      setTradeOpen(true);
    },
    [instruments]
  );

  useEffect(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage) return;
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) void fetchNextPage();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]);

  const detailTicker = selectedItem?.primaryTicker ?? null;

  return (
    <div className="flex min-h-screen flex-col bg-primary">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-2xl">
          <AssetClassToggle
            value="stocks"
            onChange={(value) => {
              if (value === "crypto") navigate("/alerts");
            }}
          />

          {isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <p className="py-12 text-center text-text-secondary">
              No stock alerts yet
            </p>
          )}

          <div className="mt-4 space-y-3">
            {items.map((item) => {
              const ticker = item.primaryTicker;
              return (
              <StockNewsTile
                key={item.id}
                item={item}
                onDetails={() => setSelectedItem(item)}
                onTrade={ticker ? () => openTrade(ticker) : undefined}
              />
              );
            })}
          </div>

          {hasNextPage && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isFetchingNextPage && (
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              )}
            </div>
          )}
        </div>
      </div>

      <ReferralBottomNav />

      <StockNewsDetailDrawer
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(open) => {
          if (!open) setSelectedItem(null);
        }}
        onTrade={detailTicker ? () => openTrade(detailTicker) : undefined}
      />

      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        displayTicker={tradeTarget?.displayTicker}
        allowedExchangeCodes={tradeTarget?.allowedExchangeCodes}
        symbolByExchangeCode={tradeTarget?.symbolByExchangeCode}
        open={tradeOpen}
        onOpenChange={(open) => {
          setTradeOpen(open);
          if (!open) {
            setTradeSymbol(null);
            setTradeTicker(null);
          }
        }}
        onGoToPosition={() => {
          setTradeOpen(false);
          navigate("/positions");
        }}
      />
    </div>
  );
};
